import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { LogOut, ChevronRight, Menu } from "lucide-react";
import _ from "lodash";

import { getDoc, doc } from "firebase/firestore";
import { auth, db } from "../../firebase.js";
import MainNav from "../../components/MainNav";
import "../../style/global.css";

const routeLabels = {
  "/venues": "Locaties",
  "/threads": "Berichten",
  "/membership": "Abonnementen",
  "/stats": "Statistieken",
  "/requests": "Aanvragen",
  "/account": "Account",
};

function Breadcrumbs() {
  const location = useLocation();
  const [subLabel, setSubLabel] = useState("");
  const segments = location.pathname.split("/").filter(Boolean);
  const subId = segments[1] || null;

  useEffect(() => {
    if (!subId) {
      setSubLabel("");
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "locations", subId)).then((snap) => {
      if (cancelled) return;
      setSubLabel(snap.exists() ? snap.data().name || subId : subId);
    }).catch(() => {
      if (!cancelled) setSubLabel(subId);
    });
    return () => { cancelled = true; };
  }, [subId]);

  if (segments.length === 0) return null;

  const basePath = `/${segments[0]}`;
  const baseLabel = routeLabels[basePath] || segments[0];

  return (
    <nav className="flex items-center gap-1.5 text-sm text-text-secondary">
      <span className={segments.length > 1 ? "hover:text-text-primary cursor-pointer" : "text-text-primary font-medium"}>{baseLabel}</span>
      {segments.length > 1 && (
        <>
          <ChevronRight size={14} className="text-text-tertiary" />
          <span className="text-text-primary font-medium truncate max-w-[200px]">{subLabel || "…"}</span>
        </>
      )}
    </nav>
  );
}

function App(props) {
  const [initialized, setInitialized] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isPublic = _.includes(props.publicRoutes, location.pathname) || location.pathname.startsWith("/upload/");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  function toggleSidebar() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar-collapsed", String(next));
      } catch {}
      return next;
    });
  }

  async function logout() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error(err);
    }
    navigate("/login");
  }

  useEffect(() => {
    return onAuthStateChanged(auth, () => setInitialized(true));
  }, []);

  if (!initialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-base">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Laden...</p>
        </div>
      </div>
    );
  }

  if (isPublic) {
    return (
      <>
        <Helmet bodyAttributes={{ class: ["Page"] }} />
        {props.children}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base">
      <Helmet bodyAttributes={{ class: ["Page"] }} />

      <MainNav
        isPublic={isPublic}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        mobileOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
      />

      <div className={`transition-all duration-normal ${sidebarCollapsed ? "lg:pl-sidebar-collapsed" : "lg:pl-sidebar"}`}>
        <header className="sticky top-0 z-20 bg-bg-surface/80 backdrop-blur-md border-b border-border h-topbar flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast lg:hidden"
              title="Menu"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <Breadcrumbs />
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/account"
              className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-text-secondary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
              title="Account"
            >
              <div className="w-7 h-7 rounded-full bg-accent-muted flex items-center justify-center text-xs font-medium text-accent shrink-0">
                {auth?.currentUser?.email?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="hidden sm:inline text-text-primary font-medium truncate max-w-[150px]">
                {auth?.currentUser?.displayName || auth?.currentUser?.email}
              </span>
            </Link>
            <button
              onClick={logout}
              className="p-2 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
              title="Uitloggen"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </header>

        <main className="mx-auto max-w-content p-4 sm:p-6 lg:p-8">{props.children}</main>
      </div>
    </div>
  );
}

export default App;
